import { useQuery } from "@tanstack/react-query";
import PrivacyPolicyContent from "./PrivacyPolicyContent";
import TermsOfServiceContent from "./TermsOfServiceContent";
import { sanitizeTermsHtml } from "%/sanitizeHtml";
import {
  getEffectiveTerms,
  type TermsTargetRole,
  type TermsType,
} from "~/services/common/terms";

interface Props {
  targetRole: TermsTargetRole;
  termsType: TermsType;
}

/**
 * 약관 본문 스타일 (가입 모달 · /policy 공용).
 *
 * Tailwind preflight 가 ol/ul 의 list-style 을 지워버리므로 번호 서식을 명시해야 한다.
 * 원문(docx)의 번호 서식이 1. → a. → i. 순이라 중첩 깊이별로 맞춰둔다.
 */
export const TERMS_CONTENT_CLASS = `text-sm leading-relaxed text-gray-800 break-words
  [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-[16px] [&_h2]:font-bold
  [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-[14px] [&_h3]:font-bold
  [&_p]:my-2
  [&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc
  [&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal
  [&_ol_ol]:list-[lower-alpha] [&_ol_ol_ol]:list-[lower-roman]
  [&_li]:my-1 [&_li]:pl-0.5
  [&_table]:my-3 [&_table]:block [&_table]:overflow-x-auto [&_table]:w-full
  [&_table]:border-collapse [&_table]:text-xs
  [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:p-1.5 [&_th]:align-top
  [&_td]:border [&_td]:border-gray-200 [&_td]:p-1.5 [&_td]:align-top
  [&_a]:text-orange-500 [&_a]:underline`;

/**
 * 약관 본문 렌더링 — DB(어드민 등록본) 우선, 없으면 기존 하드코딩 본문으로 폴백.
 *
 * 가입 동의 모달과 /policy 가 같은 본문을 보도록 하기 위한 공용 컴포넌트.
 * 이용약관은 원문 텍스트가 없어 아직 SVG 이미지(TermsOfServiceContent)로 폴백된다.
 */
export function TermsDocContent({ targetRole, termsType }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["terms-effective", targetRole, termsType],
    queryFn: () => getEffectiveTerms(targetRole, termsType),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <p className="py-10 text-sm text-center text-gray-400">불러오는 중...</p>
    );
  }

  if (!data?.contentHtml) {
    return termsType === "TERMS_TYPE_PRIVACY" ? (
      <PrivacyPolicyContent />
    ) : (
      <TermsOfServiceContent />
    );
  }

  // 버전·시행일 표기는 열람 페이지(/policy)에만 둔다.
  // 가입 동의 모달에서는 내부 버전 표기가 이용자에게 의미가 없어 본문만 보여준다.
  return (
    <div
      className={TERMS_CONTENT_CLASS}
      dangerouslySetInnerHTML={{
        __html: sanitizeTermsHtml(data.contentHtml),
      }}
    />
  );
}

export default TermsDocContent;
